import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { 
  BookOpen, Users, FileText, ClipboardCheck, Plus, BarChart3, GraduationCap, 
  CheckCircle2, Edit, Trash2, Video, ArrowLeft, Settings, Layers, Save, X,
  FileVideo, FileType, Link as LinkIcon, ChevronDown, ChevronRight, Upload,
  MessageSquare, Pin, Eye, Download, ExternalLink, Clock, Award
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const TrainerCourseManagement = ({ onMenuClick }) => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [students, setStudents] = useState([]);
  const [resources, setResources] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [discussions, setDiscussions] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Expanded states
  const [expandedModules, setExpandedModules] = useState({});
  const [selectedThread, setSelectedThread] = useState(null);
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  
  // Dialogs
  const [showModuleDialog, setShowModuleDialog] = useState(false);
  const [showLessonDialog, setShowLessonDialog] = useState(false);
  const [showResourceDialog, setShowResourceDialog] = useState(false);
  const [showReplyDialog, setShowReplyDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEvalResultsDialog, setShowEvalResultsDialog] = useState(false);
  
  // Edit states
  const [editingModule, setEditingModule] = useState(null);
  const [editingLesson, setEditingLesson] = useState(null);
  const [editingResource, setEditingResource] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [evalResults, setEvalResults] = useState(null);
  
  // Forms
  const [moduleForm, setModuleForm] = useState({ title: '', description: '' });
  const [lessonForm, setLessonForm] = useState({ module_id: '', name: '', content: '', video_url: '', lesson_type: 'text', duration_minutes: 30 });
  const [resourceForm, setResourceForm] = useState({ title: '', description: '', resource_type: 'pdf', external_url: '' });
  const [replyContent, setReplyContent] = useState('');

  useEffect(() => {
    fetchCourseData();
  }, [courseId]);

  const fetchCourseData = async () => {
    try {
      const courseRes = await api.get(`/courses/${courseId}`);
      setCourse(courseRes.data);
      setModules(courseRes.data.modules || []);
      
      // Fetch all data in parallel
      const [analyticsRes, resourcesRes, evalsRes, discussionsRes] = await Promise.all([
        api.get(`/trainer/courses/${courseId}/analytics`).catch(() => ({ data: null })),
        api.get(`/courses/${courseId}/resources`).catch(() => ({ data: [] })),
        api.get(`/courses/${courseId}/evaluations`).catch(() => ({ data: [] })),
        api.get(`/courses/${courseId}/discussions`).catch(() => ({ data: [] }))
      ]);
      
      if (analyticsRes.data) {
        setStudents(analyticsRes.data.students || []);
        setAnalytics({
          total_students: analyticsRes.data.total_students || 0,
          avg_progress: analyticsRes.data.avg_progress || 0,
          completion_rate: analyticsRes.data.assignment_completion_rate || 0,
          avg_grade: (analyticsRes.data.evaluation_success_rate / 5) || 0,
          assignments_pending: analyticsRes.data.assignments_pending || 0
        });
      }
      
      setResources(resourcesRes.data || []);
      setEvaluations(evalsRes.data || []);
      setDiscussions(discussionsRes.data || []);
      
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Erreur lors du chargement du cours');
    } finally {
      setLoading(false);
    }
  };

  // === Module CRUD ===
  const handleSaveModule = async () => {
    if (!moduleForm.title) { toast.error('Titre requis'); return; }
    try {
      if (editingModule) {
        await api.put(`/modules/${editingModule.id}`, { title: moduleForm.title, description: moduleForm.description });
        toast.success('Module mis à jour');
      } else {
        await api.post(`/courses/${courseId}/modules`, moduleForm);
        toast.success('Module créé');
      }
      setShowModuleDialog(false);
      setEditingModule(null);
      setModuleForm({ title: '', description: '' });
      fetchCourseData();
    } catch (error) { toast.error('Erreur'); }
  };

  // === Lesson CRUD ===
  const handleSaveLesson = async () => {
    if (!lessonForm.name || (!lessonForm.module_id && !editingLesson)) { toast.error('Champs requis'); return; }
    try {
      if (editingLesson) {
        await api.put(`/lessons/${editingLesson.id}`, lessonForm);
        toast.success('Leçon mise à jour');
      } else {
        await api.post(`/modules/${lessonForm.module_id}/lessons`, lessonForm);
        toast.success('Leçon créée');
      }
      setShowLessonDialog(false);
      setEditingLesson(null);
      setLessonForm({ module_id: '', name: '', content: '', video_url: '', lesson_type: 'text', duration_minutes: 30 });
      fetchCourseData();
    } catch (error) { toast.error('Erreur'); }
  };

  // === Resource CRUD ===
  const handleSaveResource = async () => {
    if (!resourceForm.title) { toast.error('Titre requis'); return; }
    try {
      if (editingResource) {
        await api.put(`/resources/${editingResource.id}`, resourceForm);
        toast.success('Ressource mise à jour');
      } else {
        await api.post(`/courses/${courseId}/resources`, resourceForm);
        toast.success('Ressource créée');
      }
      setShowResourceDialog(false);
      setEditingResource(null);
      setResourceForm({ title: '', description: '', resource_type: 'pdf', external_url: '' });
      fetchCourseData();
    } catch (error) { toast.error('Erreur'); }
  };

  const handleDeleteResource = async (id) => {
    try {
      await api.delete(`/resources/${id}`);
      toast.success('Ressource supprimée');
      fetchCourseData();
    } catch (error) { toast.error('Erreur'); }
  };

  // === Forum Actions ===
  const handleReply = async () => {
    if (!replyContent || !selectedThread) { toast.error('Contenu requis'); return; }
    try {
      await api.post(`/discussions/${selectedThread.id}/replies`, { content: replyContent });
      toast.success('Réponse envoyée');
      setReplyContent('');
      setShowReplyDialog(false);
      // Refresh thread
      const res = await api.get(`/discussions/${selectedThread.id}`);
      setSelectedThread(res.data);
    } catch (error) { toast.error('Erreur'); }
  };

  const handlePinThread = async (threadId) => {
    try {
      await api.put(`/discussions/${threadId}/pin`);
      toast.success('Discussion épinglée/désépinglée');
      fetchCourseData();
    } catch (error) { toast.error('Erreur'); }
  };

  const handleDeleteThread = async (threadId) => {
    try {
      await api.delete(`/discussions/${threadId}`);
      toast.success('Discussion supprimée');
      setSelectedThread(null);
      fetchCourseData();
    } catch (error) { toast.error('Erreur'); }
  };

  const openThread = async (thread) => {
    try {
      const res = await api.get(`/discussions/${thread.id}`);
      setSelectedThread(res.data);
    } catch (error) { toast.error('Erreur de chargement'); }
  };

  // === Evaluation Results ===
  const openEvalResults = async (evalId) => {
    try {
      const res = await api.get(`/evaluations/${evalId}/results`);
      setEvalResults(res.data);
      setShowEvalResultsDialog(true);
    } catch (error) { toast.error('Erreur de chargement'); }
  };

  // === Delete handler ===
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.type === 'module') await api.delete(`/modules/${deleteTarget.id}`);
      else await api.delete(`/lessons/${deleteTarget.id}`);
      toast.success('Supprimé');
      setShowDeleteDialog(false);
      setDeleteTarget(null);
      fetchCourseData();
    } catch (error) { toast.error('Erreur'); }
  };

  const toggleModule = (id) => setExpandedModules(prev => ({ ...prev, [id]: !prev[id] }));
  
  const getLessonTypeIcon = (type) => {
    switch (type) {
      case 'video': return <FileVideo className="w-4 h-4 text-red-500" />;
      case 'pdf': return <FileType className="w-4 h-4 text-orange-500" />;
      case 'link': return <LinkIcon className="w-4 h-4 text-blue-500" />;
      default: return <FileText className="w-4 h-4 text-slate-500" />;
    }
  };

  const getResourceIcon = (type) => {
    switch (type) {
      case 'pdf': return <FileType className="w-5 h-5 text-red-500" />;
      case 'slides': return <FileText className="w-5 h-5 text-orange-500" />;
      case 'video': return <FileVideo className="w-5 h-5 text-purple-500" />;
      case 'link': return <ExternalLink className="w-5 h-5 text-blue-500" />;
      default: return <FileText className="w-5 h-5 text-slate-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header title="Gestion du Cours" onMenuClick={onMenuClick} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" className="mb-4" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Retour
          </Button>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white text-xl font-bold">
                {course?.name?.substring(0, 2).toUpperCase() || 'C'}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{course?.name}</h1>
                <p className="text-slate-600 dark:text-slate-400">{course?.description}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
          {[
            { icon: Users, value: analytics?.total_students || 0, label: 'Étudiants', color: 'violet' },
            { icon: BarChart3, value: `${analytics?.avg_progress || 0}%`, label: 'Progression', color: 'emerald' },
            { icon: GraduationCap, value: analytics?.avg_grade?.toFixed(1) || 0, label: 'Moyenne', color: 'blue' },
            { icon: Layers, value: modules.length, label: 'Modules', color: 'amber' },
            { icon: FileText, value: resources.length, label: 'Ressources', color: 'rose' },
            { icon: ClipboardCheck, value: evaluations.length, label: 'Évaluations', color: 'purple' }
          ].map((stat, i) => (
            <Card key={i} className={`bg-gradient-to-br from-${stat.color}-50 to-${stat.color}-100/50 dark:from-${stat.color}-900/20 dark:to-${stat.color}-900/10`}>
              <CardContent className="p-4 text-center">
                <stat.icon className={`w-6 h-6 text-${stat.color}-600 mx-auto mb-2`} />
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{stat.value}</p>
                <p className="text-xs text-slate-600 dark:text-slate-400">{stat.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 grid w-full max-w-2xl grid-cols-6">
            <TabsTrigger value="overview">Aperçu</TabsTrigger>
            <TabsTrigger value="modules">Modules</TabsTrigger>
            <TabsTrigger value="resources">Ressources</TabsTrigger>
            <TabsTrigger value="evaluations">Évaluations</TabsTrigger>
            <TabsTrigger value="discussions">Discussion</TabsTrigger>
            <TabsTrigger value="students">Étudiants</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg">Modules</CardTitle>
                  <Button size="sm" onClick={() => { setEditingModule(null); setModuleForm({ title: '', description: '' }); setShowModuleDialog(true); }}>
                    <Plus className="w-4 h-4 mr-1" /> Ajouter
                  </Button>
                </CardHeader>
                <CardContent className="space-y-3">
                  {modules.length === 0 ? (
                    <p className="text-center text-slate-500 py-4">Aucun module</p>
                  ) : modules.map((m, i) => (
                    <div key={m.id} className="flex items-center gap-3 p-3 rounded-lg border hover:bg-slate-50 dark:hover:bg-slate-800">
                      <div className="w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center text-violet-600 font-bold text-sm">{i + 1}</div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-900 dark:text-white">{m.name}</p>
                        <p className="text-xs text-slate-500">{m.lessons?.length || 0} leçons</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-lg">Top Étudiants</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {students.slice(0, 5).map((s, i) => (
                    <div key={s.id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer"
                      onClick={() => navigate(`/trainer/students/${s.id}`)}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${i === 0 ? 'bg-amber-500' : i === 1 ? 'bg-slate-400' : 'bg-slate-300'}`}>{i + 1}</div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-900 dark:text-white">{s.name}</p>
                        <Progress value={s.progress} className="h-1.5 mt-1" />
                      </div>
                      <Badge variant="outline">{s.grade?.toFixed(1)}/20</Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Modules Tab */}
          <TabsContent value="modules">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Gestion des Modules</CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { setEditingLesson(null); setLessonForm({ module_id: '', name: '', content: '', video_url: '', lesson_type: 'text', duration_minutes: 30 }); setShowLessonDialog(true); }}>
                    <Plus className="w-4 h-4 mr-1" /> Leçon
                  </Button>
                  <Button onClick={() => { setEditingModule(null); setModuleForm({ title: '', description: '' }); setShowModuleDialog(true); }}>
                    <Plus className="w-4 h-4 mr-1" /> Module
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {modules.length === 0 ? (
                  <div className="text-center py-12">
                    <Layers className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                    <p className="text-slate-500 mb-4">Aucun module</p>
                    <Button onClick={() => setShowModuleDialog(true)}><Plus className="w-4 h-4 mr-2" /> Créer</Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {modules.map((m, idx) => (
                      <div key={m.id} className="border rounded-xl overflow-hidden">
                        <div className="p-4 bg-slate-50 dark:bg-slate-800 flex items-center justify-between cursor-pointer" onClick={() => toggleModule(m.id)}>
                          <div className="flex items-center gap-3">
                            {expandedModules[m.id] ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                            <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center text-violet-600 font-bold">{idx + 1}</div>
                            <div>
                              <h3 className="font-semibold">{m.name}</h3>
                              <p className="text-sm text-slate-500">{m.lessons?.length || 0} leçons</p>
                            </div>
                          </div>
                          <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                            <Button variant="outline" size="sm" onClick={() => { setLessonForm(prev => ({ ...prev, module_id: m.id.toString() })); setShowLessonDialog(true); }}>
                              <Plus className="w-4 h-4 mr-1" /> Leçon
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => { setEditingModule(m); setModuleForm({ title: m.name, description: m.description || '' }); setShowModuleDialog(true); }}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => { setDeleteTarget({ type: 'module', id: m.id, name: m.name }); setShowDeleteDialog(true); }}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        {expandedModules[m.id] && (
                          <div className="p-4 space-y-2">
                            {(!m.lessons || m.lessons.length === 0) ? <p className="text-center text-slate-500 py-4">Aucune leçon</p> : m.lessons.map((l, li) => (
                              <div key={l.id} className="flex items-center gap-3 p-3 rounded-lg bg-white dark:bg-slate-900 border hover:border-violet-200">
                                <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-sm text-slate-500">{li + 1}</div>
                                {getLessonTypeIcon(l.lesson_type)}
                                <div className="flex-1">
                                  <p className="font-medium">{l.name}</p>
                                  <p className="text-xs text-slate-500">{l.lesson_type} • {l.duration_minutes || 0} min</p>
                                </div>
                                <Button variant="ghost" size="sm" onClick={() => { setEditingLesson(l); setLessonForm({ module_id: m.id.toString(), name: l.name, content: l.content || '', video_url: l.video_url || '', lesson_type: l.lesson_type || 'text', duration_minutes: l.duration_minutes || 30 }); setShowLessonDialog(true); }}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" className="text-red-600" onClick={() => { setDeleteTarget({ type: 'lesson', id: l.id, name: l.name }); setShowDeleteDialog(true); }}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Resources Tab */}
          <TabsContent value="resources">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Ressources du Cours ({resources.length})</CardTitle>
                <Button onClick={() => { setEditingResource(null); setResourceForm({ title: '', description: '', resource_type: 'pdf', external_url: '' }); setShowResourceDialog(true); }}>
                  <Plus className="w-4 h-4 mr-2" /> Ajouter
                </Button>
              </CardHeader>
              <CardContent>
                {resources.length === 0 ? (
                  <div className="text-center py-12">
                    <Upload className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                    <p className="text-slate-500 mb-4">Aucune ressource</p>
                    <Button onClick={() => setShowResourceDialog(true)}><Plus className="w-4 h-4 mr-2" /> Ajouter</Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {resources.map(r => (
                      <div key={r.id} className="p-4 rounded-xl border hover:border-violet-300 hover:shadow-sm transition-all">
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                            {getResourceIcon(r.resource_type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-slate-900 dark:text-white truncate">{r.title}</h4>
                            <p className="text-xs text-slate-500 truncate">{r.description || 'Pas de description'}</p>
                            <Badge variant="outline" className="mt-2 text-xs">{r.resource_type}</Badge>
                          </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-4 pt-3 border-t">
                          {r.external_url && (
                            <Button variant="outline" size="sm" onClick={() => window.open(r.external_url, '_blank')}>
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => { setEditingResource(r); setResourceForm({ title: r.title, description: r.description || '', resource_type: r.resource_type, external_url: r.external_url || '' }); setShowResourceDialog(true); }}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteResource(r.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Evaluations Tab */}
          <TabsContent value="evaluations">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Évaluations ({evaluations.length})</CardTitle>
                <Button onClick={() => navigate('/trainer/create-evaluation')}>
                  <Plus className="w-4 h-4 mr-2" /> Créer
                </Button>
              </CardHeader>
              <CardContent>
                {evaluations.length === 0 ? (
                  <div className="text-center py-12">
                    <ClipboardCheck className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                    <p className="text-slate-500 mb-4">Aucune évaluation</p>
                    <Button onClick={() => navigate('/trainer/create-evaluation')}><Plus className="w-4 h-4 mr-2" /> Créer</Button>
                  </div>
                ) : (
                  <div className="divide-y">
                    {evaluations.map(ev => (
                      <div key={ev.id} className="py-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                          <ClipboardCheck className="w-6 h-6 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-slate-900 dark:text-white">{ev.title}</h4>
                          <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                            <span>{ev.question_count} questions</span>
                            <span>Score passage: {ev.passing_score}%</span>
                            <span>{ev.submission_count} soumissions</span>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <Badge className={ev.pass_rate >= 70 ? 'bg-emerald-100 text-emerald-700' : ev.pass_rate >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>
                            {ev.pass_rate}% réussite
                          </Badge>
                          <span className="text-sm text-slate-500">Moy: {ev.avg_score}%</span>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => openEvalResults(ev.id)}>
                            <Eye className="w-4 h-4 mr-1" /> Résultats
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => navigate('/trainer/create-evaluation')}>
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Discussions Tab */}
          <TabsContent value="discussions">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Thread List */}
              <Card className="lg:col-span-1">
                <CardHeader><CardTitle>Discussions</CardTitle></CardHeader>
                <CardContent className="space-y-2 max-h-[600px] overflow-y-auto">
                  {discussions.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">Aucune discussion</p>
                  ) : discussions.map(d => (
                    <div key={d.id} 
                      className={`p-3 rounded-lg cursor-pointer transition-all ${selectedThread?.id === d.id ? 'bg-violet-100 dark:bg-violet-900/30 border-violet-300' : 'hover:bg-slate-50 dark:hover:bg-slate-800 border-transparent'} border`}
                      onClick={() => openThread(d)}>
                      <div className="flex items-start gap-2">
                        {d.is_pinned && <Pin className="w-4 h-4 text-amber-500 flex-shrink-0 mt-1" />}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-slate-900 dark:text-white truncate">{d.title}</h4>
                          <p className="text-xs text-slate-500 mt-1">{d.author} • {d.reply_count} réponses</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Thread Detail */}
              <Card className="lg:col-span-2">
                {selectedThread ? (
                  <>
                    <CardHeader className="flex flex-row items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          {selectedThread.is_pinned && <Pin className="w-4 h-4 text-amber-500" />}
                          <CardTitle>{selectedThread.title}</CardTitle>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">Par {selectedThread.author} • {selectedThread.author_role}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => handlePinThread(selectedThread.id)}>
                          <Pin className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteThread(selectedThread.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg mb-4">
                        <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{selectedThread.content}</p>
                      </div>
                      
                      <h4 className="font-medium mb-3">Réponses ({selectedThread.replies?.length || 0})</h4>
                      <div className="space-y-3 max-h-80 overflow-y-auto mb-4">
                        {selectedThread.replies?.map(r => (
                          <div key={r.id} className={`p-3 rounded-lg ${r.is_trainer_reply ? 'bg-violet-50 dark:bg-violet-900/20 border-l-4 border-violet-500' : 'bg-white dark:bg-slate-900 border'} ${r.is_pinned ? 'ring-2 ring-amber-400' : ''}`}>
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm">{r.author}</span>
                                {r.is_trainer_reply && <Badge className="bg-violet-100 text-violet-700 text-xs">Formateur</Badge>}
                                {r.is_pinned && <Pin className="w-3 h-3 text-amber-500" />}
                              </div>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={async () => { await api.put(`/replies/${r.id}/pin`); openThread(selectedThread); }}>
                                  <Pin className="w-3 h-3" />
                                </Button>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-600" onClick={async () => { await api.delete(`/replies/${r.id}`); toast.success('Supprimé'); openThread(selectedThread); }}>
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                            <p className="text-sm text-slate-600 dark:text-slate-400">{r.content}</p>
                          </div>
                        ))}
                      </div>
                      
                      <Button onClick={() => setShowReplyDialog(true)} className="w-full">
                        <MessageSquare className="w-4 h-4 mr-2" /> Répondre
                      </Button>
                    </CardContent>
                  </>
                ) : (
                  <CardContent className="flex items-center justify-center h-96">
                    <p className="text-slate-500">Sélectionnez une discussion</p>
                  </CardContent>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* Students Tab */}
          <TabsContent value="students">
            <Card>
              <CardHeader><CardTitle>Étudiants ({students.length})</CardTitle></CardHeader>
              <CardContent>
                {students.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">Aucun étudiant</p>
                ) : (
                  <div className="divide-y">
                    {students.map(s => (
                      <div key={s.id} className="py-4 flex items-center gap-4 hover:bg-slate-50 dark:hover:bg-slate-800 -mx-4 px-4 cursor-pointer" onClick={() => navigate(`/trainer/students/${s.id}`)}>
                        <div className="w-10 h-10 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center text-violet-600 font-bold">{s.name.charAt(0)}</div>
                        <div className="flex-1">
                          <p className="font-medium">{s.name}</p>
                          <div className="flex items-center gap-4 mt-1">
                            <div className="flex items-center gap-2">
                              <Progress value={s.progress} className="h-1.5 w-24" />
                              <span className="text-xs text-slate-500">{s.progress}%</span>
                            </div>
                            <Badge variant="outline">{s.grade?.toFixed(1) || 0}/20</Badge>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Module Dialog */}
      <Dialog open={showModuleDialog} onOpenChange={setShowModuleDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingModule ? 'Modifier' : 'Créer'} un module</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label>Titre *</Label><Input value={moduleForm.title} onChange={(e) => setModuleForm(p => ({ ...p, title: e.target.value }))} /></div>
            <div><Label>Description</Label><Textarea value={moduleForm.description} onChange={(e) => setModuleForm(p => ({ ...p, description: e.target.value }))} rows={3} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModuleDialog(false)}>Annuler</Button>
            <Button onClick={handleSaveModule}><Save className="w-4 h-4 mr-2" />{editingModule ? 'Enregistrer' : 'Créer'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lesson Dialog */}
      <Dialog open={showLessonDialog} onOpenChange={setShowLessonDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editingLesson ? 'Modifier' : 'Créer'} une leçon</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            {!editingLesson && <div><Label>Module *</Label><Select value={lessonForm.module_id} onValueChange={(v) => setLessonForm(p => ({ ...p, module_id: v }))}><SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger><SelectContent>{modules.map(m => <SelectItem key={m.id} value={m.id.toString()}>{m.name}</SelectItem>)}</SelectContent></Select></div>}
            <div><Label>Nom *</Label><Input value={lessonForm.name} onChange={(e) => setLessonForm(p => ({ ...p, name: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Type</Label><Select value={lessonForm.lesson_type} onValueChange={(v) => setLessonForm(p => ({ ...p, lesson_type: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="text">Texte</SelectItem><SelectItem value="video">Vidéo</SelectItem><SelectItem value="pdf">PDF</SelectItem><SelectItem value="link">Lien</SelectItem></SelectContent></Select></div>
              <div><Label>Durée (min)</Label><Input type="number" value={lessonForm.duration_minutes} onChange={(e) => setLessonForm(p => ({ ...p, duration_minutes: parseInt(e.target.value) || 0 }))} /></div>
            </div>
            {lessonForm.lesson_type === 'video' && <div><Label>URL Vidéo</Label><Input value={lessonForm.video_url} onChange={(e) => setLessonForm(p => ({ ...p, video_url: e.target.value }))} /></div>}
            <div><Label>Contenu</Label><Textarea value={lessonForm.content} onChange={(e) => setLessonForm(p => ({ ...p, content: e.target.value }))} rows={5} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLessonDialog(false)}>Annuler</Button>
            <Button onClick={handleSaveLesson}><Save className="w-4 h-4 mr-2" />{editingLesson ? 'Enregistrer' : 'Créer'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resource Dialog */}
      <Dialog open={showResourceDialog} onOpenChange={setShowResourceDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingResource ? 'Modifier' : 'Ajouter'} une ressource</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label>Titre *</Label><Input value={resourceForm.title} onChange={(e) => setResourceForm(p => ({ ...p, title: e.target.value }))} /></div>
            <div><Label>Description</Label><Textarea value={resourceForm.description} onChange={(e) => setResourceForm(p => ({ ...p, description: e.target.value }))} rows={2} /></div>
            <div><Label>Type</Label><Select value={resourceForm.resource_type} onValueChange={(v) => setResourceForm(p => ({ ...p, resource_type: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="pdf">PDF</SelectItem><SelectItem value="slides">Slides</SelectItem><SelectItem value="video">Vidéo</SelectItem><SelectItem value="link">Lien externe</SelectItem><SelectItem value="document">Document</SelectItem></SelectContent></Select></div>
            <div><Label>URL externe</Label><Input value={resourceForm.external_url} onChange={(e) => setResourceForm(p => ({ ...p, external_url: e.target.value }))} placeholder="https://..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResourceDialog(false)}>Annuler</Button>
            <Button onClick={handleSaveResource}><Save className="w-4 h-4 mr-2" />{editingResource ? 'Enregistrer' : 'Ajouter'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reply Dialog */}
      <Dialog open={showReplyDialog} onOpenChange={setShowReplyDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Répondre à la discussion</DialogTitle></DialogHeader>
          <div className="py-4">
            <Textarea value={replyContent} onChange={(e) => setReplyContent(e.target.value)} placeholder="Votre réponse..." rows={5} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReplyDialog(false)}>Annuler</Button>
            <Button onClick={handleReply}><MessageSquare className="w-4 h-4 mr-2" /> Envoyer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Evaluation Results Dialog */}
      <Dialog open={showEvalResultsDialog} onOpenChange={setShowEvalResultsDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Résultats - {evalResults?.quiz?.title}</DialogTitle></DialogHeader>
          <div className="py-4">
            {evalResults && (
              <>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{evalResults.stats.total}</p><p className="text-sm text-slate-500">Participants</p></CardContent></Card>
                  <Card className="bg-emerald-50"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-emerald-700">{evalResults.stats.passed}</p><p className="text-sm text-emerald-600">Réussis</p></CardContent></Card>
                  <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{evalResults.stats.avg_score}%</p><p className="text-sm text-slate-500">Moyenne</p></CardContent></Card>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {evalResults.results.map(r => (
                    <div key={r.id} className="flex items-center gap-4 p-3 border-b">
                      <div className="flex-1">
                        <p className="font-medium">{r.student_name}</p>
                        <p className="text-sm text-slate-500">{r.score_20}/20</p>
                      </div>
                      <Badge className={r.passed ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>{r.passed ? 'Réussi' : 'Échec'}</Badge>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          <DialogFooter><Button onClick={() => setShowEvalResultsDialog(false)}>Fermer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>Supprimer "{deleteTarget?.name}" ? Cette action est irréversible.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TrainerCourseManagement;
